import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ROOT_URL } from "../../api/ApiConfig";

function authHeaders(getState) {
  const token = getState().loginToken?.response?.token;
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
}

function businessIdFromState(getState) {
  return getState().loginToken?.response?.user?.business_id;
}

/** Normalize GET/PATCH response bodies from /business/:id/ticket-from-email */
function ticketFromEmailFromPayload(payload) {
  if (payload == null || typeof payload !== "object") {
    return "";
  }
  const raw =
    payload?.data?.ticket_from_email ??
    payload?.data?.data?.ticket_from_email ??
    payload?.ticket_from_email ??
    payload?.result?.ticket_from_email ??
    payload?.data?.email ??
    payload?.email;
  if (raw == null) return "";
  return String(raw).trim();
}

export const fetchBusinessTicketFromEmail = createAsyncThunk(
  "businessTicketFromEmail/fetch",
  async (_, { getState, rejectWithValue }) => {
    const businessId = businessIdFromState(getState);
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    try {
      const response = await axios.get(
        `${ROOT_URL}/business/${businessId}/ticket-from-email`,
        authHeaders(getState),
      );
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return rejectWithValue({ notFound: true, status: 404 });
      }
      return rejectWithValue(
        error.response?.data || { message: error.message },
      );
    }
  },
);

export const patchBusinessTicketFromEmail = createAsyncThunk(
  "businessTicketFromEmail/patch",
  async ({ ticket_from_email }, { getState, rejectWithValue }) => {
    const businessId = businessIdFromState(getState);
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    if (!getState().loginToken?.response?.user?._id) {
      return rejectWithValue({ message: "Not authenticated" });
    }
    try {
      const response = await axios.patch(
        `${ROOT_URL}/business/${businessId}/ticket-from-email`,
        { ticket_from_email: ticket_from_email.trim() },
        authHeaders(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message },
      );
    }
  },
);

const initialState = {
  ticket_from_email: "",
  fetchLoading: false,
  saveLoading: false,
  error: null,
  fetchAttempted: false,
};

const businessTicketFromEmailSlice = createSlice({
  name: "businessTicketFromEmail",
  initialState,
  reducers: {
    setBusinessTicketFromEmailLocal: (state, action) => {
      state.ticket_from_email = action.payload ?? "";
    },
    clearBusinessTicketFromEmailError: (state) => {
      state.error = null;
    },
    resetBusinessTicketFromEmail: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBusinessTicketFromEmail.pending, (state) => {
        state.fetchLoading = true;
        state.error = null;
      })
      .addCase(fetchBusinessTicketFromEmail.fulfilled, (state, action) => {
        state.fetchLoading = false;
        state.fetchAttempted = true;
        state.ticket_from_email = ticketFromEmailFromPayload(action.payload);
      })
      .addCase(fetchBusinessTicketFromEmail.rejected, (state, action) => {
        state.fetchLoading = false;
        state.fetchAttempted = true;
        if (action.payload?.notFound) {
          state.ticket_from_email = "";
          state.error = null;
        } else {
          state.error = action.payload;
        }
      })
      .addCase(patchBusinessTicketFromEmail.pending, (state) => {
        state.saveLoading = true;
        state.error = null;
      })
      .addCase(patchBusinessTicketFromEmail.fulfilled, (state, action) => {
        state.saveLoading = false;
        const fromApi = ticketFromEmailFromPayload(action.payload);
        const fromArg = action.meta?.arg?.ticket_from_email;
        state.ticket_from_email = String(
          fromApi || fromArg || "",
        ).trim();
      })
      .addCase(patchBusinessTicketFromEmail.rejected, (state, action) => {
        state.saveLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setBusinessTicketFromEmailLocal,
  clearBusinessTicketFromEmailError,
  resetBusinessTicketFromEmail,
} = businessTicketFromEmailSlice.actions;

export default businessTicketFromEmailSlice.reducer;
